import { List } from 'immutable';
import { denormalize } from 'normalizr';
import { ActionCreators } from 'redux-undo-immutable';
import { addFeatureFlags } from 'react-redux-feature-flags';
import html2canvas from 'html2canvas';
import {
  getMetadata,
  getAdStates,
  getKeyframes,
  getComponents,
  getAssetIds,
  getAdId,
  getAssets,
  getAssetItemsFiltered,
  getSelectedAdstate,
  getCurrentAdStateSelectedComponents,
  getActiveFonts,
  getVariants,
} from 'Selectors';
import { makeCalculateStyle } from 'Selectors/animation';
import { resizeBox1ToFitBox2 } from 'Reducers/ad/utils';
import { updateComponentSettings, seedUndoHistory } from 'Actions/editActions';
import { resizeComponent, moveComponent } from 'Actions/styleActions';
import { loadDefaultFonts, fetchFontsInUse } from 'Actions/fontActions';

import { togglePublishModal } from 'Actions/UIActions';

import { batchUndoableActions } from 'Reducers/batchReducer';

import { setReduxAssetFolder, setReduxFilterString, loadReduxFolderAssets } from 'Actions/folderAssetActions';

import { mapToOldAd, getAssetIdFromString } from 'Legacy/adMapper';
import { extractMetadata, parseConfiguration, normalizeAd } from 'Legacy/translator';
import { reviveAdStates, reviveComponents, reviveKeyframes, reviveVariants } from 'Records/revivers';

import adDataSchema from '../schemas/adDataSchema';
import saveStatuses from '../config/saveStatuses';

export function loadReduxFolders(folders) {
  return {
    type: 'LOAD_FOLDERS',
    folders,
  };
}

export function loadReduxBreadcrumbs(breadcrumbs) {
  return {
    type: 'LOAD_BREADCRUMBS',
    breadcrumbs,
  };
}

export function loadReduxSharedUsers(sharedUsers) {
  return {
    type: 'LOAD_SHAREDUSERS',
    sharedUsers,
  };
}

export const loadAd = ({ adStates, components, keyframes, variants }) => ({
  type: 'LOAD_AD_DATA',
  adStates,
  components,
  keyframes,
  variants,
});

export const loadMetadata = loadedAdData => ({
  type: 'LOAD_METADATA',
  loadedAdData,
});

export const loadAdDataFailed = () => ({
  type: 'LOAD_LOAD_METADATA_FAILED',
});

export const loadAssets = assets => ({
  type: 'LOAD_ASSETS',
  assets,
});

export const addAsset = asset => ({
  type: 'ADD_ASSET',
  asset,
});

export const updateAdCss = css => ({
  type: 'UPDATE_AD_CSS',
  css,
});

export const updateCreativeFormat = formatId => ({
  type: 'UPDATE_CREATIVE_FORMAT',
  formatId,
});

export const toggleMaintainAspectRatio = () => ({
  type: 'TOGGLE_MAINTAIN_ASPECT_RATIO',
});

export const toggleRequiresFluidPlus = () => ({
  type: 'TOGGLE_REQUIRES_FLUID_PLUS',
});

export const toggleRevealFormat = () => ({
  type: 'TOGGLE_REVEAL_FORMAT',
});

const resizeComponentToFitImage = (state, component, posterAsset) => {
  const calculateStyle = makeCalculateStyle();
  const adState = getSelectedAdstate(state);
  const { currentTime } = adState;

  const currentStyle = calculateStyle(state, { component, currentTime });
  const deltaX = posterAsset.width - currentStyle.width;
  const deltaY = posterAsset.height - currentStyle.height;
  return resizeComponent({
    deltaX,
    deltaY,
    rotation: 0,
    cardinal: 'SE',
    skipHistory: true,
  });
};

const addAssetsAndUpdatePosterAsset = (state, component, dispatch, newAssetsRecords) => {
  const assetIds = newAssetsRecords.map(a => a.id).toList();
  const newAssets = newAssetsRecords.toJS();
  const posterAsset = newAssets.find(a => a.mime_type.includes('image'));
  const oldSrc = component.settings.get('src');
  const src = posterAsset && posterAsset.asset_url ? posterAsset.asset_url : oldSrc;
  const fitAssetToLayer = state.getIn(['ui', 'fitAssetToLayer']);

  newAssets.map(asset => dispatch(addAsset(asset))); // newAssets
  const actions = [updateComponentSettings(component, { assetIds, src })];

  if (!fitAssetToLayer && posterAsset) {
    actions.push(resizeComponentToFitImage(state, component, posterAsset));
  } else if (posterAsset) {
    const calculateStyle = makeCalculateStyle();
    const adState = getSelectedAdstate(state);
    const { currentTime } = adState;
    const currentStyle = calculateStyle(state, { component, currentTime });
    const layerWidth = currentStyle.width;
    const layerHeight = currentStyle.height;
    const assetWidth = posterAsset.width;
    const assetHeight = posterAsset.height;

    const {
      deltaWidth,
      deltaHeight,
      deltaX,
      deltaY,
    } = resizeBox1ToFitBox2({
      width1: layerWidth,
      height1: layerHeight,
      width2: assetWidth,
      height2: assetHeight,
    });

    actions.push(resizeComponent({
      deltaX: deltaWidth,
      deltaY: deltaHeight,
      rotation: 0,
      cardinal: 'SE',
      skipHistory: true,
    }));
    actions.push(moveComponent({
      deltaX,
      deltaY,
      skipHistory: false,
    }));
  }

  dispatch(batchUndoableActions(actions));
};

const assetsToKeep = (state, component) => {
  const assets = state.get('assets');
  // the type of assets we were just looking at: images or videos
  const replaceMimeTypes = state.getIn(['ui', 'assetModalMimeTypes']);
  const currentAssetIds = component.settings.get('assetIds');
  const currentAssets = assets.filter(a => currentAssetIds.includes(a.id));
  // eslint-disable-next-line max-len
  return currentAssets.reduce((arr, asset) => (!replaceMimeTypes.includes(asset.mime_type) ? arr.concat([asset]) : arr), []);
};

const updateAssetForComponent = (selectedAssets, state, dispatch) => {
  const component = getCurrentAdStateSelectedComponents(state).first();
  const newAssetsRecords = selectedAssets.concat(assetsToKeep(state, component));
  addAssetsAndUpdatePosterAsset(state, component, dispatch, newAssetsRecords);
};

const getAssetIdsForVideo = (src, state, dispatch) => {
  const videoOrder = ['mp4_vbr', 'mp4_high', 'mp4_main', 'mp4_base', 'webm', 'ogg', '3gp', 'flv', 'm3u8'];
  fetch(src).then(response => response.json()).then((videoMap) => { // eslint-disable-line no-undef
    const folderAssets = state.get('folderAssets');
    const assetIds = videoOrder.reduce((a, type) => {
      const videoUrl = videoMap[type];
      return videoUrl ? a.concat([getAssetIdFromString(videoUrl)]) : a;
    }, []);
    const assetsFromIds = folderAssets.filter(asset => assetIds.includes(asset.id));
    updateAssetForComponent(assetsFromIds, state, dispatch);
  });
};

const getAssetIdsForUploadedVideo = (component, src, posterAssets, state, dispatch) => {
  const videoOrder = ['mp4_vbr', 'mp4_high', 'mp4_main', 'mp4_base', 'webm', 'ogg', '3gp', 'flv', 'm3u8'];
  fetch(src).then(response => response.json()).then((videoMap) => { // eslint-disable-line no-undef
    const folderAssets = state.get('folderAssets');
    const assetIds = videoOrder.reduce((a, type) => {
      const videoUrl = videoMap[type];
      return videoUrl ? a.concat([getAssetIdFromString(videoUrl)]) : a;
    }, []);
    // eslint-disable-next-line max-len
    const assetsFromIds = folderAssets.filter(asset => assetIds.includes(asset.id)).concat(posterAssets);
    const newAssetsRecords = assetsFromIds.concat(assetsToKeep(state, component));
    addAssetsAndUpdatePosterAsset(state, component, dispatch, newAssetsRecords);
  });
};

export const updateAssetForFirstSelectedComponent = () => (dispatch, getState) => {
  const state = getState();
  const filteredAssets = getAssetItemsFiltered(state);
  const selectedAssets = filteredAssets.filter(asset => asset.selected);
  if (selectedAssets.size === 1 && selectedAssets.getIn(['0', 'mime_type']) === 'text/plain') {
    getAssetIdsForVideo(selectedAssets.getIn(['0', 'asset_url']), state, dispatch);
  } else {
    updateAssetForComponent(selectedAssets, state, dispatch);
  }
};

export const fetchFolderAssets = (folderId, dispatch) => {
  fetch(`/folders/${folderId}`, { // eslint-disable-line no-undef
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  }).then(response => response.json(),
  ).then((json) => {
    const {
      assets,
      folders,
      breadcrumbs,
      sharedUsers,
    } = json;
    dispatch(setReduxAssetFolder(folderId));
    dispatch(loadReduxFolderAssets(List(assets)));
    dispatch(loadReduxFolders(List(folders)));
    dispatch(loadReduxBreadcrumbs(List(breadcrumbs)));
    dispatch(loadReduxSharedUsers(List(sharedUsers)));
    dispatch(setReduxFilterString(''));

    dispatch(ActionCreators.clearHistory());
    dispatch(seedUndoHistory());
    loadDefaultFonts(dispatch);
  }).catch((response) => {
    console.log('You failed, again!'); //eslint-disable-line
    console.log(response); //eslint-disable-line
  });
};

export const fetchFolderUploads = (folderId, uploadId) => (dispatch, getState) => {
  fetch(`/folders/${folderId}`, { // eslint-disable-line no-undef
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  }).then(response => response.json())
    .then((json) => {
      const {
        assets,
      } = json;
      dispatch(loadReduxFolderAssets(List(assets)));
      const state = getState();
      const filteredAssets = getAssetItemsFiltered(state).filter(a => a.upload_id === uploadId);
      const adComponents = getComponents(state);
      const component = adComponents.filter(c => (c.settings.get('upload_id') === uploadId && c.settings.get('src') === null)).first();
      if (component) {
        const posterAsset = filteredAssets.filter(a => a.mime_type.includes('image'));
        if (component.type === 'video') {
          const videoAsset = filteredAssets.filter(a => a.name.endsWith('.vpk'));
          dispatch(updateComponentSettings(component, { name: videoAsset.first().name.replace(/\.[^.$]+$/, '') }));
          getAssetIdsForUploadedVideo(component, videoAsset.getIn(['0', 'asset_url']), posterAsset, state, dispatch);
        } else {
          dispatch(updateComponentSettings(component, { name: posterAsset.first().name.replace(/\.[^.$]+$/, '') }));
          addAssetsAndUpdatePosterAsset(state, component, dispatch, posterAsset);
        }
      }
    });
};

const processAdJson = (adData, dispatch) => {
  const {
    assets,
    features,
  } = adData;

  const normalizedData = normalizeAd(parseConfiguration(adData));

  dispatch(loadAd({
    components: reviveComponents(normalizedData.entities.components),
    keyframes: reviveKeyframes(normalizedData.entities.keyframes),
    adStates: reviveAdStates(normalizedData.entities.adStates),
    variants: reviveVariants(normalizedData.entities.variants),
  }));

  const metadata = extractMetadata(adData);

  dispatch(loadMetadata(metadata));
  fetchFolderAssets(metadata.currentAssetFolder, dispatch);

  dispatch(addFeatureFlags(features));
  dispatch(loadAssets(assets));
  dispatch(fetchFontsInUse());
};

export const fetchAd = id => (dispatch) => {
  fetch(`/build/ads/${id}`, { // eslint-disable-line no-undef
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
    },
    credentials: 'include',
  }).then(response => response.json(),
  ).then((adData) => {
    processAdJson(adData, dispatch);
  });
};

const sendSaveRequest = () => (dispatch, getState) => {
  const state = getState();
  const adStateIds = getAdStates(state).toArray().map(adState => adState.key);
  const entities = {
    keyframes: getKeyframes(state).toJS(),
    components: getComponents(state).toJS(),
    adStates: getAdStates(state).toJS(),
    variants: getVariants(state).toJS(),
  };
  const adData = denormalize(adStateIds, adDataSchema(), entities);
  const css = getMetadata(state).get('css');
  const id = getMetadata(state).get('id');
  const formatId = getMetadata(state).get('formatId');
  const requiresFluidPlus = getMetadata(state).get('requiresFluidPlus');
  const maintainAspectRatio = getMetadata(state).get('maintainAspectRatio');
  const revealFormat = getMetadata(state).get('revealFormat');
  const templateVersion = getMetadata(state).get('templateVersion');

  const uniqueAssetIds = [...new Set(getAssetIds(state))];
  const activeFonts = getActiveFonts(state);
  const activeFontsHref = activeFonts.map(font => font.href);
  const body = {
    id,
    ad: mapToOldAd(adData, css, requiresFluidPlus, templateVersion, getAssets(state), uniqueAssetIds, activeFontsHref, maintainAspectRatio, formatId, revealFormat, activeFonts.toJS()),
  };

  return fetch(`/build/ads/${id}`, // eslint-disable-line no-undef
    {
      method: 'PUT',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content, // eslint-disable-line no-undef
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
};

const sendSaveScreenGrabRequest = () => (dispatch, getState) => {
  const state = getState();
  const id = getMetadata(state).get('id');
  const cavnasElement = document.querySelector('#canvasPanel');
  const width = cavnasElement.clientWidth;
  const height = cavnasElement.clientHeight;
  const scale = 2;
  const viewport = document.querySelector('.wve_viewport');

  html2canvas(cavnasElement, { width, height, scale, removeContainer: true, useCORS: true }).then((canvas) => {
    const screenshot = canvas.toDataURL({ format: 'png', quality: 0.9 });
    const offsetLeft = viewport.getBoundingClientRect().left - viewport.offsetLeft;
    const body = { id, ad: { screenshot, offsetLeft, width, height, pixelRatio: scale } };
    return fetch(`/build/ads/${id}/screen_sync`, // eslint-disable-line no-undef
      {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content, // eslint-disable-line no-undef
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      },
    );
  });
};

export const saveAndPreviewAd = () => (dispatch, getState) => {
  const state = getState();
  return dispatch(sendSaveRequest()).then(() => {
    const previewUrl = `${window.location.origin}/build/ads/${getAdId(state)}/preview`; // eslint-disable-line no-undef
    const previewWindow = window.open(previewUrl, '_blank'); // eslint-disable-line no-undef
    previewWindow.opener = null;
  });
};

export const saveAd = () => (dispatch, getState) => {
  const state = getState();

  const saveStatus = state.getIn(['ui', 'saveStatus']);
  if (saveStatus === saveStatuses.adSaving) {
    dispatch({ type: 'AD_SAVING_STILL' });
    return;
  }
  if (saveStatus !== saveStatuses.adSaveReady) return;
  dispatch({ type: 'AD_SAVING' });

  dispatch(sendSaveRequest()).then((response) => {
    if (response.ok) {
      dispatch(sendSaveScreenGrabRequest());
      return;
    }
    throw new Error('Ad save failed');
  })
  .then(() => {
    setTimeout(() => dispatch({ type: 'AD_SAVE_SUCCESS' }), 2000);
    setTimeout(() => dispatch({ type: 'AD_SAVE_COMPLETED' }), 3500);
  })
  .catch(() => {
    setTimeout(() => dispatch({ type: 'AD_SAVE_ERROR' }), 2000);
    setTimeout(() => dispatch({ type: 'AD_SAVE_COMPLETED' }), 3500);
  });
};

export const publishAd = () => (dispatch, getState) => {
  const state = getState();
  const id = getMetadata(state).get('id');

  dispatch(togglePublishModal());
  return dispatch(sendSaveRequest()).then(() => {
    fetch(`/build/ads/publish?ad_id=${id}`, { method: 'POST', // eslint-disable-line no-undef
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content, // eslint-disable-line no-undef
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }).then(() => {
      dispatch({ type: 'PUBLISH_AD' });
    });
  });
};

export const adPublished = () => (dispatch) => {
  setTimeout(() => dispatch({ type: 'AD_PUBLISHED' }), 1000);
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/actions/loadActions.js